import React, { useState } from "react";
import { IPagination } from "../../data/AppInterface";
import { ORDER_BY } from "../../utils/AppConstants";
import {
  arrowDownIcon,
  arrowUpIcon,
  sideIconLink,
} from "../../assets/icons/SvgIconList";
import {
  dateFormat,
  getLocalDate,
  getLocalTime,
  getTextContentOnly,
  limitString,
  timeFormat,
} from "../../utils/AppFunctions";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { PER_PAGE_ROWS } from "../../config/AppConfig";
import { PaginationFooter } from "./index";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import { cn } from "../../lib";
import { ICSwitch } from "../form";
import MaskedValue from "./ICMaskedValue";

export const TABLE_FIELD_ALIGN = {
  center: 1,
  right: 2,
  left: 3,
};

export const TABLE_FIELD_TYPE = {
  text: 1,
  status: 2,
  switch: 3,
  date: 4,
  custom: 5,
  currency: 6,
  format: 7,
  time: 8,
  mask: 9,
};

type TTableFieldAlign =
  (typeof TABLE_FIELD_ALIGN)[keyof typeof TABLE_FIELD_ALIGN];

type TTableFieldType = (typeof TABLE_FIELD_TYPE)[keyof typeof TABLE_FIELD_TYPE];

export interface ITableFields<T> {
  name: string;
  key: string;
  sort: boolean;
  type: TTableFieldType;
  align: TTableFieldAlign;
  flex: string;
  checkedValue?: string | boolean | number;
  className?: string;
  handleCheck?: (item: T) => void;
  statusLabelPair?: Record<string | number, string>;
  statusColorClassPair?: Record<string | number, string>;
  labelComponent?: JSX.Element;
  component?: (item: T) => JSX.Element;
  limit?: number;
  currencyPrefix?: string;
  format?: (item: ITabelFieldViewProps<T>) => string | number | JSX.Element;
  drillDown?: boolean;
  rowIndex?: number;
  colIndex?: number;
  title?: string;
  maskingRegex?: RegExp;
  onlyMasked?: boolean;
  disabled?: (item: T) => boolean;
  assistiveText?: string;
}

export interface ICustomTableProps<T> {
  fields: ITableFields<T>[];
  totalFlex: string;
  data: T[];
  pagination?: IPagination;
  handleChangePerPageRows?: (perPageRows: number) => void;
  handleChangePage?: (page: number) => void;
  handleSort: (field: ITableFields<T>) => void;
  isLoading?: boolean;
  isHover?: boolean;
}

export interface ITabelFieldViewProps<T> {
  field: ITableFields<T>;
  data: any;
}

const getTextFieldAlignClass = (align: TTableFieldAlign) => {
  switch (align) {
    case TABLE_FIELD_ALIGN.center:
      return "text-center";
    case TABLE_FIELD_ALIGN.left:
      return "text-left";
    case TABLE_FIELD_ALIGN.right:
      return "text-right";
    default:
      return "text-center";
  }
};

interface IMaskedValue<T> {
  value: string;
  regex: RegExp;
}

const getRowsToDisplay = (pagination: IPagination) => {
  if (pagination.totalItems === 0) {
    return pagination.perPageRows;
  }
  return calculateRowsToDisplay(pagination);
};
function calculateRowsToDisplay(pagination: IPagination) {
  const totalDisplayedItems = pagination.currentPage * pagination.perPageRows;
  const remainingItems =
    pagination.totalItems -
    (pagination.currentPage - 1) * pagination.perPageRows;

  if (pagination.totalItems > totalDisplayedItems) {
    return pagination.perPageRows;
  } else if (remainingItems < 0) {
    return pagination.perPageRows;
  } else {
    return remainingItems;
  }
}

const getTextFieldJustifyClass = (align: TTableFieldAlign) => {
  switch (align) {
    case TABLE_FIELD_ALIGN.center:
      return "justify-center";
    case TABLE_FIELD_ALIGN.left:
      return "justify-start";
    case TABLE_FIELD_ALIGN.right:
      return "justify-end";
    default:
      return "justify-center";
  }
};
const getArrowIcon = (orderBy: string) => {
  if (orderBy === ORDER_BY.desc) {
    return <div className="h-5 w-5">{arrowDownIcon}</div>;
  } else {
    return <div className="h-5 w-5">{arrowUpIcon}</div>;
  }
};
const tableLabelView = <T extends object>(
  fields: ITableFields<T>[],
  handleSort: (field: ITableFields<T>) => void,
  sortBy: string,
  orderBy: string,
  totalFlex: string,
  isHover?: boolean
) => {
  return (
    <tr className={cn(`px-4 py-3 border-b`, { [`${totalFlex}`]: totalFlex })}>
      {fields.filter((isItem) => isItem.drillDown).length ? (
        <span className="w-4">&nbsp;</span>
      ) : (
        ""
      )}
      {fields
        .filter(
          (labelDrillDownItem) =>
            !labelDrillDownItem.drillDown &&
            (labelDrillDownItem.key !== "action" || isHover
              ? labelDrillDownItem.key !== "action"
              : labelDrillDownItem.key === "action")
        )
        .map(
          (
            {
              name,
              sort,
              align,
              className,
              key,
              flex,
              labelComponent,
              assistiveText,
            },
            index
          ) => {
            return (
              <th
                scope="col"
                key={name + index + "t-header"}
                onClick={() => (sort ? handleSort(fields[index]) : {})}
                className={cn(`item-center font-medium`, { [`${flex}`]: flex })}
              >
                {labelComponent || (
                  <span
                    className={cn(`flex items-center`, {
                      [`${className}`]: className,
                      [`${getTextFieldJustifyClass(align)}`]:
                        getTextFieldJustifyClass(align),
                      "cursor-pointer": sort === true,
                    })}
                    title={assistiveText}
                  >
                    {name}
                    {sortBy === key ? getArrowIcon(orderBy) : <span></span>}
                  </span>
                )}
              </th>
            );
          }
        )}
    </tr>
  );
};

const getTableFieldFromType = <T extends object>(
  fieldData: ITabelFieldViewProps<T>
) => {
  switch (fieldData.field.type) {
    case TABLE_FIELD_TYPE.text:
      return getTableFieldTextInput(fieldData);
    case TABLE_FIELD_TYPE.status:
      return getTableFieldStatus(fieldData);
    case TABLE_FIELD_TYPE.switch:
      return getTableFieldSwitch(fieldData);
    case TABLE_FIELD_TYPE.date:
      return getTableFieldDate(fieldData);
    case TABLE_FIELD_TYPE.custom:
      return getTableCustomField(fieldData);
    case TABLE_FIELD_TYPE.currency:
      return getTableFieldCurrency(fieldData);
    case TABLE_FIELD_TYPE.format:
      return getTableFieldFormat(fieldData);
    case TABLE_FIELD_TYPE.time:
      return getTableFieldTime(fieldData);
    case TABLE_FIELD_TYPE.mask:
      return getTableFieldMaskValue(fieldData);
  }
};

const getTableFieldFormat = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <span>{field.format ? field.format({ field, data }) : ""}</span>
);

const getTableFieldCurrency = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <span className="break-words">
    {data[field.key] ? (field?.currencyPrefix ?? "") + data[field.key] : ""}
  </span>
);

const getTableFieldTextInput = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => {
  const fieldValue = data[field.key];
  // Check if fieldValue is a string before processing
  const fieldValueAsString =
    typeof fieldValue === "string" || typeof fieldValue === "number"
      ? fieldValue.toString()
      : "-";

  return (
    <span className="break-words">
      {limitString(getTextContentOnly(fieldValueAsString), field.limit)}
    </span>
  );
};

const getTableFieldSwitch = <T extends object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <ICSwitch
    containerClassName="!mb-0 justify-center"
    checked={data[field.key] === field.checkedValue}
    onChange={() => (field.handleCheck ? field.handleCheck(data) : {})}
    disabled={field.disabled ? field.disabled(data) : undefined}
  />
);

const getTableFieldMaskValue = <T extends object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <MaskedValue
    value={data[field.key] ? data[field.key].toString() : ""}
    regex={field.maskingRegex ?? /./gm}
    onlyMasked={field.onlyMasked}
  />
);
const getStatusLabel = (field: any, data: any) => {
  if (field.statusLabelPair[data[field.key]]) {
    return field.statusLabelPair[data[field.key]];
  }
  return "-"; // Default value
};
const getTableFieldStatus = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <span
    className={cn(`px-2 rounded-full inline-block`, {
      [`${
        field.statusColorClassPair
          ? field.statusColorClassPair[data[field.key]]
          : ""
      }`]: field.statusColorClassPair,
      // FUTURE: if need to give default status bg color then uncomment
      // "bg-purple-200": !field.statusColorClassPair,
    })}
  >
    {field.statusLabelPair ? getStatusLabel(field, data) : data[field.key]}
  </span>
);

const getTableCustomField = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) =>
  field.component ? field.component(data) : <></>;

const getTableFieldDate = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => (
  <span>
    {data[field.key] ? dateFormat(getLocalDate(data[field.key])) : "-"}
  </span>
);
const getTableFieldTime = <T extends Object>({
  field,
  data,
}: ITabelFieldViewProps<T>) => {
  const localTime = getLocalTime(data[field.key]);
  return <span>{localTime ? timeFormat(localTime) : "-"}</span>;
};

const ICTable = <T extends object>({
  fields,
  data,
  pagination,
  handleChangePerPageRows,
  handleChangePage,
  handleSort,
  totalFlex,
  isLoading,
  isHover,
}: ICustomTableProps<T>) => {
  interface IColumnData {
    col: number;
    drillDownData: { newRow: number; field: ITableFields<T> | null }[];
  }

  const [rotation, setRotation] = useState(Array(fields.length).fill(0));
  const [openDropdownIndices, setOpenDropdownIndices] = useState<number[]>([]);
  const [hoverId, setHoverId] = useState<any>(null);
  const handleImageClick = (index: number) => {
    const newRotation = [...rotation];
    newRotation[index] = rotation[index] === 0 ? 90 : 0;
    setRotation(newRotation);
    if (openDropdownIndices.includes(index)) {
      setOpenDropdownIndices(openDropdownIndices.filter((i) => i !== index));
    } else {
      setOpenDropdownIndices([...openDropdownIndices, index]);
    }
  };
  const { t } = UseTranslationHook();

  const visibleFields = fields.filter((isVisible) => isVisible.drillDown);
  const doubleArray: IColumnData[] = [];
  let maxCol = 0;
  let maxRow = 0;

  for (const element of visibleFields) {
    if (element.colIndex && maxCol < element.colIndex) {
      maxCol = element.colIndex;
    }
    if (element.rowIndex && maxRow < element.rowIndex) {
      maxRow = element.rowIndex;
    }
  }
  for (let addCol = 0; addCol < maxCol; addCol++) {
    const columnData: IColumnData = {
      col: addCol + 1,
      drillDownData: [],
    };
    for (let addRow = 0; addRow < maxRow; addRow++) {
      const findRow = visibleFields.find(
        (rowItem) =>
          rowItem.rowIndex === addRow + 1 && rowItem.colIndex === columnData.col
      );
      if (findRow) {
        columnData.drillDownData.push({ newRow: addRow + 1, field: findRow });
      } else {
        columnData.drillDownData.push({ newRow: addRow + 1, field: null });
      }
    }
    doubleArray.push(columnData);
  }

  return (
    <>
      <div className="overflow-y-hidden overflow-x-scroll bg-subtle rounded-md my-4">
        <div className="custom-table">
          <table className="w-full">
            <thead>
              {tableLabelView<T>(
                fields,
                handleSort,
                pagination!?.sortBy,
                pagination!?.orderBy,
                totalFlex,
                isHover
              )}
            </thead>
            <tbody>
              {!isLoading &&
                data &&
                data.map((item, index) => (
                  <>
                    <tr
                      id={`data-row-${index}`}
                      key={JSON.stringify(item)}
                      className={cn(
                        `px-4 py-2 border-b rounded-sm ${totalFlex}             
                      `,
                        {
                          "bg-danger-muted":
                            (item as any).is_deleted === BIT_VALUE.One ||
                            (item as any).remaining_expiry_days === "0",
                          "bg-warning-muted":
                            (item as any).remaining_expiry_days >= 1 &&
                            (item as any).remaining_expiry_days <= 10,
                        }
                      )}
                      onMouseEnter={() => setHoverId(index)}
                      onMouseLeave={() => setHoverId(null)}
                    >
                      {fields.filter((drillDownIcon) => drillDownIcon.drillDown)
                        .length ? (
                        <td className="w-4 col-span-1">
                          <div
                            className="w-6 h-6"
                            onClick={() => handleImageClick(index)}
                            style={{
                              transform: `rotate(${rotation[index]}deg)`,
                              transition: "transform 0.5s ease-in-out",
                            }}
                          >
                            {sideIconLink}
                          </div>
                        </td>
                      ) : (
                        ""
                      )}

                      {fields
                        .filter(
                          (visibleItem) =>
                            !visibleItem.drillDown &&
                            (visibleItem.key !== "action" || isHover
                              ? visibleItem.key !== "action"
                              : visibleItem.key === "action")
                        )
                        .map((field, mIndex) => (
                          <td
                            data-label={field.name}
                            key={index + field.name}
                            className={`${getTextFieldAlignClass(
                              field.align
                            )} ${field.className ?? ""} ${field.flex} `}
                            title={
                              field.title
                                ? `${String(
                                    (item as Record<string, any>)[field.key]
                                  )}`
                                : undefined
                            }
                          >
                            {isHover &&
                            isHover === true &&
                            hoverId === index &&
                            mIndex ===
                              fields.length - 1 - visibleFields.length - 1 ? (
                              fields
                                .filter((keyItem) => keyItem.key === "action")
                                .map((HItem) => (
                                  <td
                                    key={HItem.key}
                                    className="flex justify-center items-center bg-gray-300 h-full w-full"
                                  >
                                    {HItem.component
                                      ? getTableCustomField({
                                          field: HItem,
                                          data: item,
                                        })
                                      : null}
                                  </td>
                                ))
                            ) : (
                              <>
                                {getTableFieldFromType({ field, data: item })}
                              </>
                            )}
                          </td>
                        ))}
                    </tr>

                    {openDropdownIndices.includes(index) && (
                      <tr>
                        <table className="w-full">
                          <tr>
                            <td className="pr-2">&nbsp;</td>
                            {doubleArray.map((aItem, index) => (
                              <td className="max-w-[100px] h-full" key={index}>
                                <div id="Dropdowntable" className="mx-2 my-1">
                                  <table>
                                    <tbody className="border-b rounded">
                                      {aItem.drillDownData.map((bItem) => (
                                        <tr key={bItem.newRow}>
                                          <td data-label={bItem.field?.name}>
                                            {item &&
                                            bItem.field &&
                                            (item as any)[bItem.field.key] &&
                                            (item as any)[bItem.field.key]
                                              .toString()
                                              .toLocaleLowerCase() !==
                                              "null" ? (
                                              <>
                                                {bItem.field ? (
                                                  getTableFieldFromType({
                                                    field: bItem.field,
                                                    data: item,
                                                  })
                                                ) : (
                                                  <span>&nbsp;</span>
                                                )}
                                              </>
                                            ) : (
                                              <span>&nbsp;</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            ))}
                          </tr>
                        </table>
                      </tr>
                    )}
                  </>
                ))}
              {isLoading &&
                [
                  ...Array(
                    pagination
                      ? getRowsToDisplay(pagination)
                      : Number(PER_PAGE_ROWS)
                  ).fill("0"),
                ].map((value, index) => (
                  <tr
                    id={`data-row-${index}`}
                    key={value}
                    className={cn(`px-4 py-2 border-b`, {
                      [`${totalFlex}`]: totalFlex,
                    })}
                  >
                    {fields.map((field, index) => (
                      <td
                        data-label={field.name}
                        key={index}
                        className={cn(`my-[6.2px]`, {
                          "mx-[5px]": field.align === TABLE_FIELD_ALIGN.center,
                          "mr-[10px]": field.align !== TABLE_FIELD_ALIGN.center,
                          [`${field.className}`]: field.className,
                          [`${field.flex}`]: field.flex,
                          [`${getTextFieldAlignClass(field.align)}`]:
                            getTextFieldAlignClass(field.align),
                        })}
                      >
                        <div
                          className={cn(`table-value-skeleton w-full h-3`, {
                            "mx-auto": field.align === TABLE_FIELD_ALIGN.center,
                          })}
                          style={{
                            maxWidth:
                              200 *
                                ((Number(fields.length) *
                                  Number(field.flex.slice(-1))) /
                                  Number(totalFlex.slice(-1))) +
                              "px",
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
          {!isLoading && data.length === 0 && (
            <div className="flex justify-center font-bold">
              {t("record_not_found")}
            </div>
          )}
        </div>
      </div>
      {pagination && (
        <PaginationFooter
          pagination={pagination}
          handleChangePerPageRows={handleChangePerPageRows}
          handleChangePage={handleChangePage}
        />
      )}
    </>
  );
};

export default ICTable;
